import {
  DXR,
  Options,
  RibbonSelectBoxItem,
  RichEdit,
  useRichEdit,
} from './dx-richedit';
import React, { useLayoutEffect, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import 'devexpress-richedit/dist/dx.richedit.css';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';

import styles from './RichEditComponent.module.scss';
import './RichEditComponent.override.scss';
import {
  addWordToDictionary,
  checkWordSpelling,
} from 'application/spell-check/spellChecker';
import {
  checkBoxTooltipKey,
  onHyperlinkClick,
} from 'pages/authorized/documents/components/richedit-helper';

export type Props = {
  onLoaded?: () => void;
  initialize?: (options: Options) => void;
  richEditRef: React.MutableRefObject<RichEdit>;
  className?: string;

  // Used to explicitly specify that the editor is always read-only.
  readOnly?: boolean;
};
export const RichEditComponent = React.forwardRef<HTMLDivElement, Props>(
  function DocumentNonLazy(props, ref) {
    const richEditWrapperRef = useRef<HTMLDivElement>(null);
    const i18n = useScopedTranslation('Page.DocumentEditor');
    useRichEdit();

    const richEdit = useRef<RichEdit>(null!);
    const richEditDisposeTimerIdRed = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);

    // Apply a custom read-only style. Disable tabs and buttons that change the document.
    // It is not possible to add read-only class to className of <RichEditComponent>, because changing RichEdit
    // styles after initialization breaks ruler and document border style.
    if (richEditWrapperRef.current && props.richEditRef && !props.readOnly) {
      if (props.richEditRef.current.readOnly) {
        richEditWrapperRef.current.classList.add(styles.readOnly);
      } else {
        richEditWrapperRef.current.classList.remove(styles.readOnly);
      }
    }

    useLayoutEffect(() => {
      // this is to prevent disposing RichEdit in React StrictMode (with double-invoking of events)
      // otherwise there's an error "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
      if (richEditDisposeTimerIdRed.current) {
        clearTimeout(richEditDisposeTimerIdRed.current);
        return;
      }

      // the createOptions() method creates an object that contains RichEdit options initialized with default values
      const options = DXR.createOptions();

      options.bookmarks!.visibility = false;
      options.bookmarks!.color = '#ff0000';

      options.confirmOnLosingChanges!.enabled = true;
      // options.confirmOnLosingChanges!.message =
      //   'Are you sure you want to perform the action? All unsaved document data will be lost.';
      options.fields!.updateFieldsBeforePrint = true;
      options.fields!.updateFieldsOnPaste = true;

      options.unit = DXR.RichEditUnit.Centimeter;

      options.view!.viewType = DXR.ViewType.PrintLayout;
      options.view!.simpleViewSettings!.paddings = {
        left: 15,
        top: 15,
        right: 15,
        bottom: 15,
      };
      options.readOnly = false;
      options.width = '100%';
      options.height = 'calc(100%)';

      options.ribbon.removeTab(DXR.RibbonTabType.View);

      options.spellCheck = {
        enabled: true,
        suggestionCount: 3,
        checkWordSpelling,
        addWordToDictionary,
      };

      const tableDesignTab = options.ribbon.getTab(
        DXR.RibbonTabType.TableDesign,
      )!;
      tableDesignTab.removeItem(
        DXR.TableDesignTabItemId.ChangeTableBorderColorRepositoryItem,
      );
      tableDesignTab.removeItem(
        DXR.TableDesignTabItemId.ChangeTableCellShading,
      );

      const tableStylesSelect = tableDesignTab.items[1] as RibbonSelectBoxItem;
      tableStylesSelect.dataSource = tableStylesSelect.dataSource.filter(
        (s) =>
          s.text !== 'Plain Table 3' &&
          s.text !== 'Plain Table 5' &&
          !s.text.includes('Grid Table 3') &&
          !s.text.includes('Grid Table 7'),
      );

      const homeTab = options.ribbon.getTab(DXR.RibbonTabType.Home)!;
      homeTab.removeItem(DXR.HomeTabItemId.Cut);
      homeTab.removeItem(DXR.HomeTabItemId.Copy);
      homeTab.removeItem(DXR.HomeTabItemId.Paste);
      homeTab.removeItem(DXR.HomeTabItemId.DecreaseFontSize);
      homeTab.removeItem(DXR.HomeTabItemId.IncreaseFontSize);

      const textStylesTab = options.ribbon.getTab(DXR.RibbonTabType.Home)!;
      textStylesTab.title = 'Text Styles';
      textStylesTab.removeItem(DXR.HomeTabItemId.ChangeFontForeColor);
      textStylesTab.removeItem(DXR.HomeTabItemId.ChangeParagraphBackColor);
      textStylesTab.removeItem(DXR.HomeTabItemId.ToggleFontSubscript);
      textStylesTab.removeItem(DXR.HomeTabItemId.ToggleFontSuperscript);

      const tableLayoutTab = options.ribbon.getTab(
        DXR.RibbonTabType.TableLayout,
      )!;
      tableLayoutTab.removeItem(DXR.TableLayoutTabItemId.AutoFitMenu);

      const filePanel = options.ribbon.getTab(DXR.RibbonTabType.File)!;

      filePanel.removeItem(DXR.FileTabItemId.OpenDocument);
      filePanel.removeItem(DXR.FileTabItemId.CreateNewDocument);

      const printButton = filePanel.getItem(DXR.FileTabItemId.PrintDocument);
      filePanel.removeItem(DXR.FileTabItemId.PrintDocument);
      filePanel.removeItem(DXR.FileTabItemId.Download);

      filePanel.insertItem(printButton!, 2);

      options.ribbon.activeTabIndex = 0;

      options.events.hyperlinkClick = onHyperlinkClick;

      if (options.fields) {
        options.fields.openHyperlinkOnClick = true;
        options.fields.createHyperlinkTooltip = function (
          hyperlinkTooltip,
          hint,
        ) {
          return hyperlinkTooltip === checkBoxTooltipKey
            ? i18n.t('check_box_tooltip')
            : hyperlinkTooltip;
        };
      }

      options.readOnly = props.readOnly ?? false;

      props.initialize?.(options);

      richEdit.current = DXR.create(richEditWrapperRef.current!, options);
      props.richEditRef.current = richEdit.current;
      richEdit.current.rangePermissionOptions.highlightRanges = true;
      richEdit.current.rangePermissionOptions.showBrackets = false;

      richEdit.current.events.contextMenuShowing.addHandler((re, args) => {
        if (!args || !re || !re.readOnly) return;

        const bookmarkMenuItem = args.contextMenu.getItem(
          DXR.ContextMenuCommandId.ShowBookmarkDialog,
        );
        const selectAllMenuItem = args.contextMenu.getItem(
          DXR.ContextMenuCommandId.SelectAll,
        );
        if (bookmarkMenuItem) bookmarkMenuItem.disabled = true;
        if (selectAllMenuItem) selectAllMenuItem.disabled = true;
      });

      setTimeout(() => {
        // delay update ribbon menu to speed up initial loading of control
        props.onLoaded?.();
      }, 1);

      return () => {
        try {
          richEditDisposeTimerIdRed.current = setTimeout(() => {
            richEdit.current?.dispose();
            richEditDisposeTimerIdRed.current = null;
          }, 10);
        } catch (e) {}
      };
    }, []);

    return (
      <div
        ref={mergeRefs([richEditWrapperRef, ref])}
        id="richEdit"
        className={props.className}
      ></div>
    );
  },
);
